import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { ConnectorAccessRequest } from "@/types";

// #279: Zugriffs-Anträge auf Connectoren.

/** Anträge auf EINEN Connector — die Sicht beim Klick auf den Connector. */
export function useConnectorAccessRequests(
  connectorId: string | undefined,
  status?: string,
): UseQueryResult<ConnectorAccessRequest[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: connectorId
      ? [...queryKeys.subResource("connectors", connectorId, "access-requests"), status ?? "all"]
      : ["resources", "connectors", "_invalid", "access-requests"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      const q = status ? `?status=${status}` : "";
      return client.get<ConnectorAccessRequest[]>(`/connectors/${connectorId}/access-requests${q}`);
    },
    enabled: !!session && !!connectorId,
  });
}

/**
 * Admin-Arbeitsvorrat: ALLE Anträge. Zustellweg für ownerlose (System-)Connectoren — dort
 * gibt es keinen persönlichen Empfänger und damit auch keine Benachrichtigung.
 */
export function useAllAccessRequests(status = "pending"): UseQueryResult<ConnectorAccessRequest[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.list("connector-access-requests"), status],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ConnectorAccessRequest[]>(`/connector-access-requests?status=${status}`);
    },
    enabled: !!session,
  });
}
